'use client';

import Link from '@eliadmin/core/src/components/link';

import type { PropsWithChildren } from 'react';

import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LuStar } from 'react-icons/lu';
import { usePathname } from 'next/navigation';

export type CategoryMenuProps = {
  className?: string;
};

export function CategoryMenu(props: PropsWithChildren<CategoryMenuProps>) {
  return (
    <ul className={cn('flex flex-row items-center gap-2', 'ea-category-menu', props.className)}>
      {props.children}
    </ul>
  );
}

export type CategoryMenuItemProps = {
  title: string;
  href?: string;
  onClick?: () => void;
  order?: number;
  isSubscription?: boolean;
  className?: {
    root?: string;
    link?: string;
  };
};

export function CategoryMenuItem(props: CategoryMenuItemProps) {
  const pathname = usePathname();
  const href = props.href ? getSafeHref(props.href) : '#';
  const isLink = props.href ? isRelativeHref(getSafeHref(props.href)) : false;
  const shouldScrollToHash = href.includes('#') && href !== '#';

  const isActive = props.href ? pathname === getSafeHref(props.href) : false;

  const linkClassName = cn(
    'flex items-center justify-center gap-2 text-sm font-bold transition-all xl:text-base',
    'ea-category-menu-item__link',
    isActive && 'ea-category-menu-item__link--active cursor-context-menu',
    !isActive && 'opacity-85 hover:opacity-100',
    props.isSubscription ? 'text-white opacity-85' : 'text-white',
    props.className?.link,
  );

  const content = (
    <>
      {props.isSubscription && (
        <LuStar size={20} className="fill-primary stroke-primary text-primary" />
      )}
      {props.title}
    </>
  );

  return (
    <li
      style={{ order: props.order }}
      className={cn('flex flex-row', 'ea-category-menu-item', props.className?.root)}
    >
      {isLink ? (
        <Link prefetchOnHover
          href={href}
          className={linkClassName}
          onClick={props.onClick}
          prefetch={!shouldScrollToHash}
          scroll={shouldScrollToHash ? true : undefined}
        >
          {content}
        </Link>
      ) : props.href ? (
        <a href={href} className={linkClassName} onClick={props.onClick}>
          {content}
        </a>
      ) : (
        <span className={linkClassName} onClick={props.onClick}>
          {content}
        </span>
      )}
    </li>
  );
}
